import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Card, CardContent } from "@/components/ui/card";
import { fetchBlogPost } from "@/lib/api";

export default function BlogPostPage({ content }) {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const isHindi = content?.locale === "hi";

  useEffect(() => {
    fetchBlogPost(slug)
      .then((item) => {
        setPost(item);
        setErrorMessage("");
      })
      .catch((error) => {
        setPost(null);
        setErrorMessage(error?.response?.data?.detail || (isHindi ? "लेख उपलब्ध नहीं है।" : "This article is currently unavailable."));
      })
      .finally(() => setLoading(false));
  }, [isHindi, slug]);

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <p className="text-sm text-stone-500" data-testid="blog-post-loading-state">{isHindi ? "लेख लोड हो रहा है..." : "Loading article..."}</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 className="font-heading text-4xl font-semibold text-stone-900" data-testid="blog-post-not-found-title">{isHindi ? "लेख नहीं मिला" : "Article not found"}</h1>
        <p className="mt-4 text-sm leading-7 text-stone-600" data-testid="blog-post-error-message">
          {errorMessage || (isHindi ? "यह लेख अभी उपलब्ध नहीं है।" : "This article is currently unavailable.")}
        </p>
        <Link to="/blog" className="mt-4 inline-flex text-sm font-semibold text-primary" data-testid="blog-post-not-found-link">
          {isHindi ? "ब्लॉग पर वापस जाएँ" : "Back to blog"}
        </Link>
      </div>
    );
  }

  const bodySections = Array.isArray(post?.body_sections) ? post.body_sections : [];

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary" data-testid="blog-post-category-text">
          {post.category}
        </p>
        <h1 className="mt-4 font-heading text-4xl font-semibold text-stone-900 sm:text-5xl" data-testid="blog-post-title-text">
          {post.title}
        </h1>
        <p className="mt-4 text-sm text-stone-500" data-testid="blog-post-meta-text">
          {post.reading_time} • {post.published_on}
        </p>
      </div>

      <div className="mt-10 grid gap-6">
        {bodySections.map((section, index) => (
          <Card key={section.heading} className="rounded-[28px] border-stone-200 bg-white shadow-sm">
            <CardContent className="space-y-5 p-8">
              <h2 className="font-heading text-2xl font-semibold text-stone-900" data-testid={`blog-post-section-title-${index + 1}`}>
                {section.heading}
              </h2>
              {(Array.isArray(section.paragraphs) ? section.paragraphs : []).map((paragraph, paragraphIndex) => (
                <p key={`${section.heading}-${paragraphIndex}`} className="text-sm leading-7 text-stone-600" data-testid={`blog-post-section-paragraph-${index + 1}-${paragraphIndex + 1}`}>
                  {paragraph}
                </p>
              ))}
              {(Array.isArray(section.bullets) ? section.bullets : []).length ? (
                <ul className="grid gap-3">
                  {(Array.isArray(section.bullets) ? section.bullets : []).map((item, bulletIndex) => (
                    <li key={`${section.heading}-${bulletIndex}`} className="rounded-[20px] border border-primary/10 bg-primary/5 px-4 py-4 text-sm text-stone-700" data-testid={`blog-post-section-bullet-${index + 1}-${bulletIndex + 1}`}>
                      {item}
                    </li>
                  ))}
                </ul>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}